import { Badge, Flex, Tag, Tooltip, Typography } from "antd";
import { ChatConversation } from "@rumble-it/common-lib/types/models/ChatConversation.interface";
import {
  chatChannelConfig,
  chatConversationStatusConfig,
  chatMessageStatusConfig,
} from "../data/chatChannelConfig";
import { formatInboxTime } from "../data/chatInbox";

const { Text } = Typography;

/**
 * A single conversation row for the inbox and the dossier communication tab:
 * channel icon, contact (driver name or number), optional license plate, the
 * last-message status glyph and a preview.
 */
export const ChatConversationListItem = ({
  conversation,
  isActive,
  onClick,
}: {
  conversation: ChatConversation;
  isActive: boolean;
  onClick: () => void;
}) => {
  const channelConfig = chatChannelConfig[conversation.channel];
  const statusConfig = chatConversationStatusConfig[conversation.status];
  const lastStatus =
    conversation.lastMessageDirection === "outbound" &&
    conversation.lastMessageOutboundStatus
      ? chatMessageStatusConfig[conversation.lastMessageOutboundStatus]
      : undefined;

  return (
    <div
      onClick={onClick}
      className={`cursor-pointer px-4 py-2.5 border-b transition-all ${
        isActive
          ? "bg-primary-900 border-l-2 border-l-primary-600"
          : "hover:bg-gray-50"
      }`}
    >
      <Flex vertical style={{ minWidth: 0 }} gap={2}>
        <Flex justify="space-between" align="center" gap={8}>
          <Flex align="center" gap={6} style={{ minWidth: 0 }}>
            <Badge dot={conversation.unhandled} offset={[-2, 2]}>
              <span
                style={{ color: `var(--ant-color-${channelConfig.color})` }}
                title={channelConfig.label}
              >
                {channelConfig.icon}
              </span>
            </Badge>
            <Text strong={conversation.unhandled} ellipsis>
              {conversation.contactName ?? conversation.contactNumber}
            </Text>
            {conversation.licensePlate ? (
              <Text
                code
                style={{ fontSize: 10, margin: 0, whiteSpace: "nowrap" }}
              >
                {conversation.licensePlate}
              </Text>
            ) : null}
          </Flex>
          <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
            {formatInboxTime(conversation.lastMessageAt)}
          </Text>
        </Flex>
        <Flex align="center" gap={4} style={{ minWidth: 0 }}>
          {statusConfig && conversation.status !== "matched" ? (
            <Tag color={statusConfig.color} style={{ fontSize: 11, margin: 0 }}>
              {statusConfig.label}
            </Tag>
          ) : null}
          {lastStatus ? (
            <Tooltip title={lastStatus.label}>
              <span
                style={{
                  fontSize: 12,
                  color: lastStatus.color ?? "var(--ant-color-text-tertiary)",
                  lineHeight: 1,
                }}
              >
                {lastStatus.icon}
              </span>
            </Tooltip>
          ) : null}
          <Text type="secondary" ellipsis style={{ fontSize: 12, flex: 1 }}>
            {conversation.lastMessagePreview ?? ""}
          </Text>
        </Flex>
      </Flex>
    </div>
  );
};
